import type { ParsedClasses, EditorBreakpoint, ParsedValue } from './types';
import { isPropertyValue, isBreakpointAwareProperty } from './types';
import { getEffectiveValue, setValue, setValuePreservingDesktop, clearValue } from './gettersSetters';
import { buildValue, buildClasses } from './builder';
import { parseValue, parseClasses } from './parser';
import { getStandaloneClassValue } from './propertyMap';

/**
 * Get just the value portion from a Tailwind class (without the prefix).
 * For standalone compound classes (like border-none, object-cover), returns
 * the semantic CSS value (e.g., 'none', 'cover') rather than the full class name.
 *
 * @example
 * getValueFromClass('px-4');        // '4'
 * getValueFromClass('text-primary'); // 'primary'
 * getValueFromClass('bg-[#ff0000]'); // '#ff0000'
 * getValueFromClass('rounded-lg');   // 'lg'
 * getValueFromClass('flex');         // 'flex' (simple standalone classes return themselves)
 * getValueFromClass('border-none');  // 'none' (compound standalone returns CSS value)
 * getValueFromClass('object-cover'); // 'cover'
 */
export function getValueFromClass(className: string): string | undefined {
  if (!className) {
    return undefined;
  }

  const parsed = parseValue(className);
  if (!parsed) {
    return undefined;
  }

  // For standalone classes, transform to semantic CSS value if applicable
  return getStandaloneClassValue(parsed.value);
}

/**
 * Get the effective Tailwind class for a property at a specific breakpoint.
 *
 * @example
 * const parsed = parseClasses('text-2xl lg:text-4xl');
 * getClassForProperty({ parsed, property: 'fontSize', breakpoint: 'base' }); // 'text-2xl'
 * getClassForProperty({ parsed, property: 'fontSize', breakpoint: 'lg' });   // 'text-4xl'
 */
export function getClassForProperty({
  parsed,
  property,
  breakpoint,
}: {
  parsed: ParsedClasses;
  property: string;
  breakpoint: EditorBreakpoint;
}): string | null {
  const value = getEffectiveValue({ parsed, property, breakpoint });
  // Don't include breakpoint prefix - return the effective class value without prefix
  return value ? buildValue(value, { includeBreakpoint: false }) : null;
}

/**
 * Check if a property has an override at a specific breakpoint.
 * Useful for showing visual indicators in the UI.
 */
export function hasOverrideAtBreakpoint({
  parsed,
  property,
  breakpoint,
}: {
  parsed: ParsedClasses;
  property: string;
  breakpoint: EditorBreakpoint;
}): boolean {
  const propertyValue = parsed[property];
  if (!isPropertyValue(propertyValue)) {
    return false;
  }

  if (breakpoint === 'base') {
    return propertyValue.base !== undefined;
  }

  return propertyValue.lg !== undefined;
}

/**
 * Create a class string update for a specific property at a specific breakpoint.
 * This is a convenience function that combines setValue + buildClasses.
 *
 * For breakpoint-aware properties (display, flexDirection, padding, etc.), this function
 * automatically preserves the desktop experience when editing mobile: if there's a base
 * value but no lg override, the old base value is promoted to lg before setting the new value.
 *
 * The value can be:
 * - A full Tailwind class (e.g., 'text-4xl', 'bg-red-500')
 * - Just the value portion (e.g., '4xl', 'red-500')
 * - An arbitrary value (e.g., '[20px]', '[#ff0000]')
 * - null to clear the property
 *
 * @example
 * // Full class (backward compatible)
 * updateClassString({ classString: 'text-2xl', property: 'fontSize', breakpoint: 'lg', value: 'text-4xl' });
 * // Returns: 'text-2xl lg:text-4xl'
 *
 * // Just the value portion
 * updateClassString({ classString: 'text-2xl', property: 'fontSize', breakpoint: 'lg', value: '4xl' });
 * // Returns: 'text-2xl lg:text-4xl'
 *
 * // Breakpoint-aware property with desktop preservation
 * updateClassString({ classString: 'grid', property: 'display', breakpoint: 'base', value: 'hidden' });
 * // Returns: 'hidden lg:grid' (desktop preserved automatically)
 */
export function updateClassString({
  classString,
  property,
  breakpoint,
  value,
}: {
  classString: string;
  property: string;
  breakpoint: EditorBreakpoint;
  value: string | null;
}): string {
  const parsed = parseClasses(classString);

  let updated: ParsedClasses;

  if (value) {
    // Try to parse as a full class first (e.g., 'text-4xl')
    const directParse = parseValue(value);

    let parsedNewValue: ParsedValue;

    if (directParse) {
      // Override the property and breakpoint to match the target
      parsedNewValue = { ...directParse, property, breakpoint };
    } else {
      // Value is just the value portion (e.g., '4xl' or '[20px]')
      const isArbitrary = value.startsWith('[') && value.endsWith(']');
      parsedNewValue = {
        type: isArbitrary ? 'arbitrary' : 'standard',
        property,
        value: isArbitrary ? value.slice(1, -1) : value,
        breakpoint,
      };
    }

    // Use preserve desktop behavior for breakpoint-aware properties
    if (isBreakpointAwareProperty(property)) {
      updated = setValuePreservingDesktop(parsed, parsedNewValue);
    } else {
      updated = setValue(parsed, parsedNewValue);
    }
  } else {
    // Clearing the value
    updated = clearValue(parsed, property, breakpoint);
  }

  return buildClasses(updated);
}

/**
 * Remove multiple properties from a class string at all breakpoints.
 * Useful for resolving conflicts when applying variants/presets.
 *
 * @example
 * removePropertiesFromClassString({
 *   classString: 'bg-red-500 lg:bg-blue-500 px-4 lg:px-8 text-white',
 *   properties: ['backgroundColor', 'textColor']
 * });
 * // Returns: 'px-4 lg:px-8'
 */
export function removePropertiesFromClassString({
  classString,
  properties,
}: {
  classString: string;
  properties: string[];
}): string {
  const parsed = parseClasses(classString);

  // Remove each property (deleting removes at all breakpoints)
  for (const property of properties) {
    delete parsed[property];
  }

  return buildClasses(parsed);
}
